import React, { useState } from 'react';
import { batch, useSelector } from 'react-redux';

import { doRequest } from '@r1-frontend/do-request';
import { saleAgentTimeSlots } from '@r1-frontend/api-domru/full-buy/v1/sale-agent/time-slots';
import { RequestDto } from '@r1-frontend/api-domru/full-buy/v1/sale-agent/time-slots/dto/RequestDto';
import { ResponseDto as GetTimeSlotsResponseDto } from '@r1-frontend/api-domru/full-buy/v1/sale-agent/time-slots/dto/ResponseDto';

import DateOfServiceEngineerVisitWrapper, {
    ESaleAgentClientName,
    TReturnType,
} from '@r1-frontend/service-engineer-visit';

import Alert from '@r1-frontend/ui-react/components/Alert';
import { Danger } from '@r1-frontend/ui-react/components/svg/main';
import { Pre } from '@r1-frontend/ui-react/components/typography/formatting';
import { H3 } from '@r1-frontend/ui-react/components/typography/heading';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import { FullWidthListContainer } from '@r1-frontend/ui-react/experimental/containers';

import { changePlanWebcabThunk, replanRequest } from '~/src/features/tariffBuilder/actions/thunk';
import CallbackContacts from '~/src/features/tariffBuilder/components/CallbackContacts';
import { useCtxDispatch } from '~/src/features/tariffBuilder/hooks';
import { TState } from '~/src/store';
import { selectDomain } from '~/src/store/selectors/domain';
import { selectTerminalResource } from '~/src/store/selectors/service';

import { ESlotsContainerState, TIME_SLOT_YK_ERROR, TSlotResult } from './SelectVisitDate';

const GA_CATEGORY_AUTH = 'constructor_authorized';

const SelectVisitDateCurrentTariff = (): JSX.Element | null => {
    const dispatch = useCtxDispatch();

    const { domain, agreementId, trId } = useSelector((state: TState) => ({
        trId: selectTerminalResource(state),
        domain: selectDomain(state),
        agreementId: state.auth.clientPersonal?.agreementId,
    }));

    const [slotResult, setSlotResult] = useState<TSlotResult>({ state: ESlotsContainerState.SHOW_SLOTS, result: null });

    const handleConfirm = (result: TReturnType | null, errorMsg?: string) => {
        setSlotResult(prevState => ({
            ...prevState,
            state: result === null ? ESlotsContainerState.SLOTS_EMPTY : ESlotsContainerState.SHOW_SLOTS,
            result,
            errorMsg,
        }));
        batch(() => {
            dispatch(replanRequest({ agreementId, selectedDate: `${result?.date}`, selectedTime: `${result?.hour}`, comment: `${result?.comment}` }));
            dispatch(changePlanWebcabThunk(trId));
        });
    };

    const getTimeSlots = (providerId: number, body: RequestDto) => {
        return doRequest.fullBuy<GetTimeSlotsResponseDto>(saleAgentTimeSlots(providerId, body));
    };

    if (!agreementId) {
        return null;
    }

    return (
        <FullWidthListContainer $size={16}>
            <H3 $withIndent={false}>Выберите дату визита инженера</H3>
            {(slotResult.state === ESlotsContainerState.SHOW_SLOTS && !slotResult.errorMsg) &&
            <Pre $whiteSpace='break-spaces'>
                <Paragraph4>
                    Инженер подключит и&nbsp;настроит оборудование примерно за&nbsp;60&nbsp;минут.
                </Paragraph4>
            </Pre>
            }
            {slotResult.state === ESlotsContainerState.SLOTS_EMPTY &&
                <Alert Icon={Danger} type={'error'}>
                    <Paragraph4>
                        {slotResult.errorMsg?.includes(TIME_SLOT_YK_ERROR)
                            ? <>Нужно согласование ТСЖ или&nbsp;УК, чтобы инженер смог подключить вам услуги.
                                 Ожидайте звонка специалиста&nbsp;&mdash; он&nbsp;поможет спланировать визит инженера</>
                            : <>На&nbsp;ближайшие 5&nbsp;дней нет свободных инженеров. Если мы&nbsp;не&nbsp;перезвоним вам сегодня, напишите в&nbsp;чат поддержки.
                                Чтобы мы&nbsp;смогли дозвониться&nbsp;&mdash; проверьте, что у&nbsp;вас отключен автоответчик и&nbsp;антиспам приложения.
                            </>
                        }
                    </Paragraph4>
                </Alert>
            }
            {slotResult.errorMsg && (
                <Alert Icon={Danger} type={'error'}>
                    <Paragraph4>
                        При подтверждении даты визита инженера произошла ошибка. Наш специалист перезвонит вам для решения вопроса.
                    </Paragraph4>
                </Alert>
            )}
            {slotResult.state === ESlotsContainerState.SHOW_SLOTS &&
                <DateOfServiceEngineerVisitWrapper
                    getTimeSlotsFunc={getTimeSlots}
                    onConfirm={handleConfirm}
                    onClickConfirm={() => null}
                    contractId={agreementId}
                    clientName={ESaleAgentClientName.FULL_BUY_SITE}
                    withComment={true}
                    gaCategory={GA_CATEGORY_AUTH}
                    domain={domain}
                />
            }
            {(slotResult.state === ESlotsContainerState.SLOTS_EMPTY || slotResult.errorMsg) &&
                <>
                    <H3 $withIndent={false}>Если остались вопросы</H3>
                    <CallbackContacts/>
                </>
            }
        </FullWidthListContainer>
    );
};

export default SelectVisitDateCurrentTariff;
